/**
 * Developer settings (board 19: API keys, webhooks, event log, allowed hosts). English source.
 */
export const developerEn = {
  nav: 'Developer',
  title: 'Developer',
  description:
    'Connect your own systems: API keys for server integrations, webhooks that tell them what happens, and the hosts Hamnivo may call.',
  apiReference: 'API reference',
  newTab: '(opens in a new tab)',
  tabs: {
    keys: 'API keys',
    webhooks: 'Webhooks',
    events: 'Event log',
    hosts: 'Allowed hosts',
  },
  reveal: {
    description: 'Store this securely; it won’t be shown again.',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Couldn’t copy. The text is selected; copy it yourself.',
    once: 'Hamnivo keeps only a fingerprint of it. If it is lost, rotate it to get a new one.',
    done: 'Done',
  },
  permissions: {
    analytics: {
      read: {
        name: 'Read reports',
        hint: 'Service-quality aggregates and the CSV export. No message content.',
      },
    },
    billing: {
      read: { name: 'Read billing', hint: 'The plan, usage against its limits and invoices.' },
    },
    webhooks: {
      read: {
        name: 'Read webhooks',
        hint: 'Endpoints, the delivery log and event types. No signing secrets.',
      },
    },
    mcp: {
      read: {
        name: 'Read through MCP',
        hint: 'Lets an AI assistant such as Claude search conversations, read what customers saw, and look up knowledge, customer memory and open hand-offs. Read only.',
      },
    },
  },
  keys: {
    title: 'API keys',
    description:
      'A key lets a server call the Hamnivo API as its own service account, with only the permissions you give it, and never more than yours.',
    readOnly: 'You can see keys, but only people who manage API keys can create or change them.',
    loading: 'Loading API keys',
    empty: 'No API keys yet.',
    neverUsed: 'Never used',
    scopeTenant: 'Whole workspace',
    scopeBrand: 'Brand: {{brand}}',
    scopeType: {
      workspace: 'One workspace',
      brand: 'One brand',
      team: 'One team',
      inbox: 'One inbox',
    },
    status: {
      active: 'Active',
      rotating: 'Being replaced',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    columns: {
      name: 'Name',
      key: 'Key',
      access: 'Access',
      status: 'Status',
      lastUsed: 'Last used',
      expires: 'Ends',
      actions: 'Actions',
    },
    create: {
      open: 'Create key',
      title: 'Create API key',
      description: 'Give the key only what the integration needs. Every key expires.',
      name: 'Name',
      nameHint: 'Something that says where it is used, such as “Order system”.',
      permissions: 'Permissions',
      notYours: 'You don’t hold this permission yourself, so you can’t give it.',
      scope: 'Where it applies',
      scopeHint: 'Billing and webhooks always cover the whole workspace.',
      expiry: 'Expires in',
      expiryHint: 'Rotate the key before it ends; the old one can keep working for a while.',
      days: '{{count}} days',
      submit: 'Create key',
    },
    rotate: {
      button: 'Rotate',
      label: 'Rotate {{name}}',
      title: 'Rotate “{{name}}”?',
      description:
        'You get a new key with the same permissions. Update your integration, then let the old key run out.',
      overlap: 'The old key keeps working for',
      overlaps: {
        '0': 'No time: it stops now',
        '60': 'One hour',
        '1440': 'One day',
        '10080': 'One week',
      },
      confirm: 'Rotate key',
      unchanged: 'The key was not rotated; the current one still works.',
    },
    revoke: {
      button: 'Revoke',
      label: 'Revoke {{name}}',
      title: 'Revoke “{{name}}”?',
      description: 'Requests with this key stop working at once. This cannot be undone.',
      confirm: 'Revoke key',
      done: '{{name}} was revoked.',
    },
    reveal: {
      title: 'API key created',
      label: 'API key for {{name}}',
    },
  },
  webhooks: {
    title: 'Webhooks',
    description:
      'Hamnivo sends signed HTTPS requests to your endpoints when something happens. Internal notes are never sent.',
    add: 'Add endpoint',
    loading: 'Loading webhook endpoints',
    empty: 'No endpoints yet. Add one to receive events.',
    noDeliveries: 'Nothing sent yet',
    eventCount: 'Events: {{count}}',
    open: 'Open',
    openLabel: 'Open {{url}}',
    created: 'Endpoint added',
    secret: 'Signing secret',
    secretLabel: 'Signing secret',
    verifiedAt: 'Test answered {{time}}',
    notVerified: 'Not tested yet',
    previousValid: 'The previous secret also signs until {{time}}.',
    blocked:
      'This endpoint’s host is not on the list of allowed hosts, so deliveries to it are refused. Add the host under Allowed hosts.',
    failingNote:
      'Failed attempts in a row: {{count}}. Deliveries are retried for about three days; an endpoint that keeps failing is turned off.',
    edit: 'Edit',
    status: {
      active: 'Active',
      failing: 'Attention needed',
    },
    disabled: {
      manual: 'Turned off',
      failing: 'Turned off: kept failing',
      gone: 'Turned off: endpoint gone',
    },
    columns: {
      url: 'Address',
      status: 'Status',
      events: 'Events',
      latest: 'Latest delivery',
      open: 'Details',
    },
    categories: {
      conversations: 'Conversations',
      messages: 'Messages',
      handoffs: 'Handoffs',
      orders: 'Orders',
      payments: 'Payments',
      refunds: 'Refunds',
      memory: 'Customer memory',
      usage: 'Usage',
      testing: 'Testing',
    },
    events: {
      conversation_created: 'A conversation started.',
      conversation_updated: 'Who answers, or the status the customer sees, changed.',
      conversation_resolved: 'A conversation was resolved.',
      conversation_reopened: 'A conversation was reopened.',
      message_created: 'A message the customer can see was sent.',
      handoff_requested: 'The conversation is waiting for a person.',
      handoff_accepted: 'A person took the conversation.',
      handoff_returned: 'The conversation went back to the AI assistant.',
      order_created: 'An order was placed.',
      order_updated: 'An order changed state.',
      payment_updated: 'A payment changed state.',
      refund_created: 'A refund was requested.',
      refund_updated: 'A refund changed state.',
      memory_deleted: 'Customer memory was deleted (the receipt, without the content).',
      usage_threshold_reached: 'Usage reached an alert threshold.',
      webhook_test: 'A test event sent from Studio or the API.',
    },
    form: {
      title: 'Add endpoint',
      editTitle: 'Edit endpoint',
      description:
        'Use an HTTPS address on the public internet. The signing secret is shown once, after you add it.',
      url: 'Endpoint address',
      urlHint: 'For example https://hooks.example.com/hamnivo',
      note: 'Description',
      events: 'Events to send',
      submit: 'Add endpoint',
      save: 'Save changes',
      saved: 'Endpoint saved.',
    },
    test: {
      button: 'Send test event',
      verified: 'Your endpoint accepted the signed test event.',
      verifiedBadge: 'Verified',
      failed: 'The test event was not accepted. See the answer below.',
    },
    rotate: {
      button: 'Rotate secret',
      title: 'Rotate the signing secret?',
      description:
        'During the overlap every request carries two signatures, one per secret, so your receiver can switch without missing events.',
      overlap: 'Keep signing with the old secret for',
      overlaps: {
        '0': 'No time: it stops now',
        '1': 'One hour',
        '24': 'One day',
        '72': 'Three days',
      },
      confirm: 'Rotate secret',
      unchanged: 'The secret was not rotated; the current one still signs.',
      revealTitle: 'New signing secret',
    },
    turnOff: {
      button: 'Turn off',
      title: 'Turn off this endpoint?',
      description:
        'Nothing more is sent to it, and deliveries waiting for a retry are cancelled. You can replay them after turning it on.',
      confirm: 'Turn off',
      done: 'Endpoint turned off.',
    },
    turnOn: {
      button: 'Turn on',
      title: 'Turn on this endpoint?',
      description: 'New events are sent again, and its failure count starts from zero.',
      confirm: 'Turn on',
      done: 'Endpoint turned on.',
    },
    replayFailed: {
      button: 'Replay failed',
      title: 'Replay failed deliveries?',
      description:
        'Every delivery to this endpoint that failed or was cancelled in the period is sent again, with its original event id.',
      since: 'Failed in the',
      windows: {
        '1': 'Last 24 hours',
        '3': 'Last 3 days',
        '7': 'Last 7 days',
      },
      confirm: 'Replay deliveries',
      done: 'Replays queued: {{count}}. Too old to send again: {{expired}}.',
    },
    delete: {
      button: 'Delete',
      title: 'Delete this endpoint?',
      description:
        'Its delivery history goes with it and its signing secrets stop working. This cannot be undone.',
      confirm: 'Delete endpoint',
      done: 'Endpoint deleted.',
    },
  },
  deliveries: {
    title: 'Delivery history',
    loading: 'Loading deliveries',
    loadingDetail: 'Loading the delivery',
    empty: 'Nothing has been sent to this endpoint yet.',
    detailTitle: 'Delivery details',
    event: 'Event',
    eventId: 'Event ID',
    statusLabel: 'Status',
    attempts: 'Attempts',
    attemptsOf: 'Attempts: {{count}} of {{max}}',
    attemptNumber: 'Attempt {{number}}',
    nextAttempt: 'Next attempt',
    replayed: 'Replay',
    answer: 'Answer:',
    preview: 'Payload preview',
    previewNote:
      'What people wrote and who they are is hidden here; your endpoint received the full event.',
    expired: 'The event is past its retention, so its content is no longer kept.',
    status: {
      pending: 'Retrying',
      succeeded: 'Delivered',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    outcomes: {
      succeeded: 'Accepted',
      http_error: 'Error answer',
      redirect: 'Redirect (not followed)',
      timeout: 'Timed out',
      network: 'Connection failed',
      tls: 'Certificate problem',
      blocked: 'Refused',
      unresolvable: 'Host not found',
    },
    replay: {
      button: 'Replay this delivery',
      title: 'Replay this delivery?',
      description: 'Send the {{event}} event again to',
      dedupe:
        'Receivers should deduplicate on the event id: a replay carries the same webhook-id as the original.',
      confirm: 'Replay delivery',
      queued: 'Replay queued.',
      turnOnFirst: 'Turn the endpoint on to replay deliveries to it.',
    },
  },
  events: {
    title: 'Event log',
    description:
      'Events sent to your endpoints, newest first, with what happened to their deliveries.',
    filter: 'Event type',
    allTypes: 'All event types',
    loading: 'Loading events',
    empty: 'No events yet. Events are recorded while at least one endpoint is on.',
    emptyFiltered: 'No events of this type yet.',
    more: 'Show older events',
    counts: 'Delivered {{succeeded}} · failed {{failed}} · retrying {{pending}}',
    columns: {
      type: 'Event',
      id: 'Event ID',
      occurred: 'Happened',
      deliveries: 'Deliveries',
    },
  },
  hosts: {
    title: 'Allowed hosts',
    description:
      'Limit the hosts Hamnivo may call for this workspace, such as your webhook endpoints. With no hosts listed, any public host is allowed.',
    always:
      'Private networks, this server and local addresses are always refused, whatever is listed here.',
    loading: 'Loading allowed hosts',
    modeAny: 'Any public host is allowed.',
    modeList: 'Only the hosts below are allowed.',
    empty: 'No hosts listed.',
    pattern: 'Host',
    patternHint: 'A host such as hooks.example.com, or *.example.com for its subdomains.',
    note: 'Note',
    remove: 'Remove {{host}}',
    removeShort: 'Remove',
    add: 'Add host',
    save: 'Save allowed hosts',
    savedList: 'Allowed hosts saved: {{count}}.',
    savedAny: 'Saved. Any public host is allowed.',
    readOnly: 'Only people who manage the network list can change it.',
    blocked: 'These endpoints are not on the list, so deliveries to them are refused:',
  },
};
